import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Form,
  SelectPicker,
  Message,
  useToaster,
  Input,
  CheckTreePicker,
} from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import { Button, Modal, Badge, SegmentedPicker } from "../../../../../ui";

const Textarea = React.forwardRef((props, ref) => (
  <Input {...props} as="textarea" ref={ref} />
));

export const CreateIssue = ({
  open,
  onClose,
  systemId,
  metadata,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const initFormValue = {
    id: systemId,
    reportable: false,
    report_users: [],
    type: "OFFLINE",
    notes: "",
    assigned: null,
    processor_type: "MMB",
  };

  const [formValue, setFormValue] = useState(initFormValue);
  const [checkTreeData, setCheckTreeData] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectData = [
    "OFFLINE",
    "MAGNET",
    "EDU",
    "Credentials",
    "Host Configuration",
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const selectProcessor = ["MMB", "HHM"].map((item) => ({
    label: item,
    value: item,
  }));

  const assignedSelectData = [
    "Remote Tech Team",
    "Cust-Password",
    "Network Team",
    "Jerome",
    "Joe",
    "Customer - Jerome",
    "Customer - Joe",
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const flattenUsers = (selected = [], tree = []) => {
    const result = new Set();

    const recurse = (nodes) => {
      for (const node of nodes) {
        if (selected.includes(node.value)) {
          if (node.children) {
            node.children.forEach((child) => result.add(child.value));
          } else {
            result.add(node.value);
          }
        } else if (node.children) {
          recurse(node.children);
        }
      }
    };

    recurse(tree);
    return Array.from(result);
  };

  const getUsers = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/network/system/users/${systemId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const internalUsers = response.data[0]
        .filter((user) => user.class === "internal")
        .map((user) => ({
          label: (
            <div style={{ display: "inline-block" }}>
              {user.first_name} {user.last_name}
              <div style={{ fontSize: "0.75em", color: "#888" }}>
                {user.email_address}
              </div>
            </div>
          ),
          value: user.email_address,
        }));

      const externalUsers = response.data[0]
        .filter((user) => user.class === "external")
        .map((user) => ({
          label: (
            <div style={{ display: "inline-block" }}>
              {user.first_name} {user.last_name}
              <div style={{ fontSize: "0.75em", color: "#888" }}>
                {user.email_address}
              </div>
            </div>
          ),
          value: user.email_address,
        }));

      const treeData = [];

      if (internalUsers.length > 0) {
        treeData.push({
          label: "Avante Users",
          value: "internal",
          children: internalUsers,
        });
      }

      if (externalUsers.length > 0) {
        treeData.push({
          label: "External Users",
          value: "external",
          children: externalUsers,
        });
      }

      setCheckTreeData(treeData);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/add/issue`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: systemId,
          issueData: {
            ...formValue,
            report_users: flattenUsers(formValue.report_users, checkTreeData),
          },
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          Issue created successfully for system {systemId}
        </Message>,
        "topCenter",
      );

      // Reset form
      setFormValue(initFormValue);
      onSuccess && onSuccess();
    } catch (error) {
      console.error("Error creating issue:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to create issue. Please try again.
        </Message>,
        "topCenter",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && systemId) {
      getUsers();
      setFormValue({
        ...initFormValue,
        id: systemId,
      });
    }
  }, [open, systemId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <ModalTitle>
          <PlusIcon style={{ marginRight: "0.5rem" }} />
          Create New Issue
          {metadata && (
            <>
              {" for "}
              <SystemBadge variant="primary">{systemId}</SystemBadge>
            </>
          )}
        </ModalTitle>
      }
      showFooter={true}
      footerActions={
        <>
          <Button variant="subtle" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={loading}>
            Create Issue
          </Button>
        </>
      }
    >
      <FormContainer
        fluid
        formValue={formValue}
        onChange={(formValue) => setFormValue(formValue)}
      >
        {metadata && (
          <SystemInfoSection>
            <InfoRow>
              <InfoLabel>Customer:</InfoLabel>
              <InfoValue>{metadata.customer_name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Site:</InfoLabel>
              <InfoValue>{metadata.site_name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Model:</InfoLabel>
              <InfoValue>{metadata.model}</InfoValue>
            </InfoRow>
          </SystemInfoSection>
        )}

        <Form.Group controlId="processor_type">
          <FormLabel>Processor Type</FormLabel>
          <Form.Control
            name="processor_type"
            accepter={SelectPicker}
            data={selectProcessor}
            searchable={false}
            block
          />
        </Form.Group>

        <Form.Group controlId="type">
          <FormLabel>Issue Type</FormLabel>
          <Form.Control
            name="type"
            accepter={SelectPicker}
            data={selectData}
            searchable={false}
            block
          />
        </Form.Group>

        <FormSection>
          <FormLabel>Reportable</FormLabel>
          <SegmentedPicker
            options={[
              { eventKey: "false", label: "No" },
              { eventKey: "true", label: "Yes" },
            ]}
            activeKey={String(formValue.reportable)}
            onSelect={(key) =>
              setFormValue((prev) => ({
                ...prev,
                reportable: key === "true",
              }))
            }
            size="small"
          />
        </FormSection>

        {formValue.reportable && (
          <Form.Group controlId="report_users">
            <FormLabel>Assigned Users</FormLabel>
            <Form.Control
              name="report_users"
              accepter={CheckTreePicker}
              data={checkTreeData}
              placeholder="Select Users"
              block
            />
          </Form.Group>
        )}

        <FormSection>
          <FormLabel>Notes</FormLabel>
          {formValue.reportable && (
            <ReportWarning>
              <RemindFillIcon size="1.2em" color="#f5a623" />
              <WarningText>
                Users will receive this note in their report
              </WarningText>
            </ReportWarning>
          )}
          <Form.Control
            name="notes"
            accepter={Textarea}
            placeholder="Describe the issue..."
            rows={4}
          />
        </FormSection>

        <FormSection>
          <FormLabel>Assigned To</FormLabel>
          <Form.Control
            name="assigned"
            placeholder="Select assignee (optional)"
            accepter={SelectPicker}
            data={assignedSelectData}
            searchable={false}
            block
          />
        </FormSection>
      </FormContainer>
    </Modal>
  );
};

// Styled Components
const FormContainer = styled(Form)`
  background: transparent !important;

  &::before,
  &::after {
    display: none !important;
  }

  .rs-form-control-wrapper {
    background: transparent !important;

    &::before,
    &::after {
      display: none !important;
    }
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SystemBadge = styled(Badge)`
  font-size: 0.9rem;
`;

const SystemInfoSection = styled.div`
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  min-width: 80px;
`;

const InfoValue = styled.span`
  color: rgba(0, 0, 0, 0.8);
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
  background: transparent !important;
  position: relative;
  z-index: 1;

  .rs-form-group {
    margin-bottom: 0;
  }

  &::before,
  &::after {
    display: none !important;
  }

  .rs-form-control-wrapper::before,
  .rs-form-control-wrapper::after {
    display: none !important;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReportWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(245, 166, 35, 0.15) !important;
  border: none;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;

  &::before,
  &::after {
    display: none !important;
  }

  * {
    position: relative;
    z-index: 2;
  }
`;

const WarningText = styled.span`
  font-size: 0.85rem;
  color: #f5a623;
  font-weight: 500;
`;
